<template>

  <v-container fluid class="page-content route-item-editor" v-if="route">

    <!------------------------------------------------------------------------->
    <!-- Route info (delivery date, name, dispatch button) -->
    <!------------------------------------------------------------------------->
    <v-row class="pa-0 ma-0">
      <v-col cols="12" md="12" lg="12" style="padding-top:0" class="d-flex flex-column align-left justify-center">
        <v-row class="pa-0 ma-0">
          <v-col cols="12" xs="12" sm="2" md="3" lg="2" class="label pa-0 ma-0 d-flex flex-column align-left justify-center">
            Delivery for:
          </v-col>
          <!-- Delivery date -->
          <v-col cols="12" xs="12" sm="8" md="9" lg="9" class="d-flex flex-column align-left justify-center delivery-date">
            <span class="pl-2">{{ deliveryDateForDisplay }}</span>
          </v-col>
        </v-row>
        <v-row class="pa-0 ma-0">
          <!-- Route name -->
          <v-col cols="2" class="d-flex pa-0 ma-0 flex-column align-left justify-center label label-route-name">
            Name:
          </v-col>
          <v-col class="d-flex flex-column align-left justify-center route-name">
            <v-text-field
                outlined
                hide-details="auto"
                v-model.trim="route.name"
                @input="emitDirty('name')"
                :disabled="!isRouteEditable" />
          </v-col>
        </v-row>
      </v-col>
      <!-- Dispatch button (and info when dispatched) -->
      <v-col cols="12" class="label">
        <template v-if="hasDispatched">
          <div class="pt-2 pb-3 dispatched-message">
            <div style="font-size:1.1em">DISPATCHED</div>
            <div class="mt-1" style="font-size:16px">{{ dispatchedDateForDisplay }}</div>

            <!-- Route Info link -->
            <div class="mt-4" align="center">
              <router-link :to="generateRouteInfoPageLink()">
                <v-btn text color="primary">
                 View Route Info
                </v-btn>
              </router-link>
            </div>
          </div>

          <!-- Dispatched route order checklist -->
          <!-- <div align="center" class="mt-2">Containing orders:</div>
          <div v-for="order in orders" :key="order.order_number" align="center" class="mt-3">
            <router-link :to="generateOrderInfoPageLink(order)">
              <v-chip
                  label
                  outlined
                  color="yellow darken-3"
                  style="cursor:pointer;">
                <v-icon left>mdi-package-variant-closed</v-icon>
                {{ order.thirdparty_order_number }}
              </v-chip>
            </router-link>
            <span style="padding-left:8px;padding-right:8px;">-</span>{{ order.order_status }}
            <v-icon color="red darken-1" v-if="order.order_status === 'BEING_SCHEDULED'">mdi-alert</v-icon>
          </div> -->

          <!-- Order Sync link -->
          <div class="mt-4" v-if="shouldSyncOrders" align="center">
            <v-progress-circular indeterminate :size="25" style="" color="orange darken-3" v-if="isOrderSyncing" />
            <v-btn text @click="syncOrders" v-else>Sync Orders</v-btn>
            <!-- tooltip -->
            <v-tooltip right>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" style="cursor:pointer;font-size:19px;color:#9c9c9c;">
                  mdi-information-outline
                </v-icon>
              </template>
              <div>Use this action to resolve any dispatched orders</div>
              <div>that are stuck in the "being scheduled" state.</div>
            </v-tooltip>
          </div>
        </template>

        <!-- Dispatch button -->
        <double-click-confirm-button
            v-else
            buttonText="Dispatch"
            buttonWidth="100%"
            buttonHeight="65"
            buttonStyle="font-size:1.14rem"
            confirmTitle="Dispatch route for delivery?"
            :confirmMessageText="dispatchConfirmMessageText"
            :confirmMessageTextLine2="dispatchConfirmMessageTextLine2"
            :disabled="!isReadyToDispatch"
            @confirmed="dispatchRoute" />
      </v-col>
    </v-row>

    <v-row class="divider"></v-row>

    <!------------------------------------------------------------------------->
    <!-- Start/End Route -->
    <!------------------------------------------------------------------------->

    <!-- Start Time -->
    <v-row class="pa-0 ma-0">
      <v-col xs="12" sm="3" md="3" lg="3" class="label d-flex flex-column align-left justify-center">
        Start at:
      </v-col>
      <v-col xs="12" sm="4" md="4" lg="5" class="d-flex flex-column align-left justify-center">
        {{ vehicleLot.name }}
      </v-col>
      <v-col xs="12" sm="5" md="5" lg="3" class="d-flex flex-column align-left justify-center">
        <!-- Start time selector -->
        <v-dialog persistent max-width="350" v-model="isStartTimeSelectorOpen">
          <!-- Activator button -->
          <template v-slot:activator="{ on, attrs }">
            <v-btn plain class="btn-select-start-time" color="cyan accent-3"
                v-bind="attrs"
                v-on="on"
                v-if="!scheduledStartTime">
              Select start time
            </v-btn>
            <v-btn plain class="btn-change-start-time" color="cyan accent-3"
                v-bind="attrs"
                v-on="on"
                :disabled="!isRouteEditable"
                v-else>
              <span class="start-time">{{ scheduledStartTimeForDisplay }}</span>
              <v-icon class="icon-change-start-time" v-if="isRouteEditable">mdi-pencil</v-icon>
            </v-btn>
          </template>
          <!-- Dialog content -->
          <v-card class="time-selector">
            <v-card-text>
              <v-time-picker
                  full-width
                  format="ampm"
                  v-model="selectedStartTime" />
            </v-card-text>
            <v-card-actions class="px-10">
              <v-btn text color="primary" @click="isStartTimeSelectorOpen = false">
                Cancel
              </v-btn>
              <v-spacer />
              <v-btn color="primary" width="100" :disabled="!this.selectedStartTime" @click="setStartTime">
                Ok
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>

    <!-- Return Time -->
    <v-row class="pa-0 ma-0">
      <v-col xs="12" sm="3" md="3" lg="3" class="label d-flex flex-column align-left justify-center">
        End at:
      </v-col>
      <v-col xs="12" sm="4" md="4" lg="5" class="d-flex flex-column align-left justify-center">
        {{ vehicleLot.name }}
      </v-col>
      <v-col xs="12" sm="5" md="5" lg="3" class="d-flex align-center justify-center return-time">
        <div class="d-flex flex-column align-center justify-center">
          <span class="return-time-subtext" v-if="route.estimated_return_time">
            Est. Return Time
          </span>
          {{ returnTimeForDisplay }}
        </div>
        <!-- return time tooltip (about wait times) -->
        <v-tooltip right v-if="route.estimated_return_time">
          <template v-slot:activator="{ on, attrs }">
            <v-icon class="icon-return-time-info" v-bind="attrs" v-on="on">
              mdi-information-outline
            </v-icon>
          </template>
          <div>Considers:</div>
          <div>Pickup wait time: {{ pickupWaitTime }} mins</div>
          <div>Dropoff wait time: {{ dropoffWaitTime }} mins</div>
        </v-tooltip>
      </v-col>
    </v-row>

    <v-row class="divider"></v-row>

    <!------------------------------------------------------------------------->
    <!-- Vehicle & Driver assigment -->
    <!------------------------------------------------------------------------->

    <!-- Vehicle Assignment -->
    <v-row class="pa-0 ma-0">
      <v-col xs="12" sm="3" md="3" lg="3" class="label d-flex flex-column align-left justify-center">
        Vehicle:
      </v-col>
      <v-col xs="12" sm="5" md="5" lg="5" class="d-flex align-center">
        <!-- Vehicle selector -->
        <v-dialog scrollable persistent fullscreen v-model="isVehicleSelectorOpen">
          <!-- Activator button -->
          <template v-slot:activator="{ on, attrs }">
            <v-btn
                v-if="!assignedVehicle"
                plain
                color="cyan accent-3"
                v-bind="attrs"
                v-on="on"
                :disabled="!scheduledStartTime">
              Assign vehicle
            </v-btn>
            <v-btn
                v-else
                plain
                color="cyan accent-3"
                class="px-0 btn-change-vehicle"
                v-bind="attrs"
                v-on="on"
                :disabled="!isRouteEditable">
              <span class="assigned-vehicle">{{ assignedVehicle }}</span>
              <v-icon v-if="isRouteEditable" class="pl-2 icon-change-vehicle">mdi-pencil</v-icon>
            </v-btn>
          </template>
          <!-- Dialog content -->
          <vehicle-status-selector
              :activeSelected="selectedVehicle"
              v-on:canceled="isVehicleSelectorOpen = false"
              v-on:selected="setAssignedVehicle" />
        </v-dialog>
      </v-col>
      <v-col xs="12" sm="2" md="3" lg="3" class="label d-flex align-center justify-center">
        <!-- Remove vehicle assignment -->
        <v-btn v-if="assignedVehicle && isRouteEditable"
            plain
            class="btn-remove-vehicle"
            color="red"
            @click="removeAssignedVehicle">
          <v-icon class="icon-remove-vehicle">mdi-close</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Driver Assignment -->
    <v-row class="pa-0 ma-0">
      <v-col xs="12" sm="3" md="3" lg="3" class="label d-flex flex-column align-left justify-center">
        Driver:
      </v-col>
      <v-col xs="12" sm="5" md="5" lg="5" class="d-flex align-center">
        <!-- Driver selector -->
        <v-dialog scrollable persistent fullscreen v-model="isDriverSelectorOpen">
          <!-- Activator button -->
          <template v-slot:activator="{ on, attrs }">
            <v-btn
                v-if="!assignedDriver"
                plain
                color="cyan accent-3"
                v-bind="attrs"
                v-on="on"
                :disabled="!scheduledStartTime">
              Assign driver
            </v-btn>
            <v-btn
                v-else
                plain
                color="cyan accent-3"
                class="px-0 btn-change-driver"
                v-bind="attrs"
                v-on="on"
                :disabled="!isRouteEditable">
              <span class="assigned-driver">{{ assignedDriver }}</span>
              <v-icon v-if="isRouteEditable" class="pl-2 icon-change-driver">mdi-pencil</v-icon>
            </v-btn>
          </template>
          <!-- Dialog content -->
          <driver-status-selector
              :activeSelected="selectedDriver"
              v-on:canceled="isDriverSelectorOpen = false"
              v-on:selected="setAssignedDriver" />
        </v-dialog>
      </v-col>
      <v-col xs="12" sm="2" md="3" lg="3" class="label d-flex align-center justify-center">
        <!-- Remove driver assignment -->
        <v-btn v-if="assignedDriver && isRouteEditable"
            plain
            class="btn-remove-driver"
            color="red"
            @click="removeAssignedDriver">
          <v-icon class="icon-remove-driver">mdi-close</v-icon>
        </v-btn>
      </v-col>
    </v-row>

  </v-container>

</template>

<script>
/* global moment */
/* eslint-disable no-unneeded-ternary */
import DoubleClickConfirmButton from '@/components/app/DoubleClickConfirmButton'
import VehicleStatusSelector from '@/components/vehicles/VehicleStatusSelector'
import DriverStatusSelector from '@/components/drivers/DriverStatusSelector'

export default {
  name: 'RouteItemEditor',

  components: {
    DoubleClickConfirmButton,
    VehicleStatusSelector,
    DriverStatusSelector
  },

  props: {
    route: {
      type: Object,
      required: true
    },
    isRouteEditable: {
      type: Boolean,
      required: true
    }
  },

  data () {
    return {
      isOrderSyncing: false,
      selectedStartTime: null,
      isStartTimeSelectorOpen: false,
      isVehicleSelectorOpen: false,
      isDriverSelectorOpen: false,
      selectedVehicle: null,
      selectedDriver: null
    }
  },

  computed: {
    activeOrg () {
      return this.$store.getters['auth/activeOrg']
    },
    defaultTimezone () {
      return this.$store.getters['app/defaultTimezone']
    },
    pickupWaitTime () {
      return (this.activeOrg) ? this.$root.getOrderPickupWaitTime(this.activeOrg.id) : ''
    },
    dropoffWaitTime () {
      return (this.activeOrg) ? this.$root.getOrderDropoffWaitTime(this.activeOrg.id) : ''
    },
    isTestRoute () {
      return this.route.is_test
    },
    orders () {
      return this.route.orders || []
    },
    lockedOrders () {
      return this.orders.filter(order => order.order_status === 'BEING_SCHEDULED')
    },
    shouldSyncOrders () {
      return this.route.route_status === 'DISPATCHED' && this.lockedOrders.length > 0
    },
    hasDispatched () {
      return this.route.route_status === 'DISPATCHED'
    },
    isReadyToDispatch () {
      // Permitted to dispatch when start time is set, route is generated, vehicle/driver assigned, and all required appts confirmed
      return !!(
        this.scheduledStartTime &&
        this.estimatedReturnTime &&
        this.assignedVehicle &&
        this.assignedDriver &&
        this.$parent.appointmentsAreSatisfied &&
        !this.$parent.regenerateRouteRequired
      )
    },
    dispatchConfirmMessageText () {
      if (this.isTestRoute) return 'This is a test route.'
      return this.route.route_status === 'DISPATCHED_EDITING'
        ? 'This will notify all relevant parties'
        : 'This will notify all relevant parties.'
    },
    dispatchConfirmMessageTextLine2 () {
      if (this.isTestRoute) return 'No parties will ne notified.'
      return this.route.route_status === 'DISPATCHED_EDITING'
        ? 'affected by the changes.'
        : null
    },
    deliveryDate () {
      return this.route.delivery_date
    },
    deliveryDateForDisplay () {
      return this.$root.utils.format(this.deliveryDate, 'ddd, MMM D, YYYY')
    },
    dispatchedDateForDisplay () {
      return (this.route.dispatched_at)
        ? this.$root.utils.format(this.route.dispatched_at, 'MMMM D, YYYY h:mm a')
        : null
    },
    scheduledStartTime () {
      return this.route.scheduled_start_time
    },
    scheduledStartTimeForDisplay () {
      return (this.route.scheduled_start_time)
        ? this.$root.utils.format(this.route.scheduled_start_time, 'h:mm a')
        : ''
    },
    estimatedReturnTime () {
      return this.route.estimated_return_time
    },
    returnTimeForDisplay () {
      return (this.estimatedReturnTime)
        ? this.$root.utils.format(this.estimatedReturnTime, 'h:mm a')
        : '-'
    },
    vehicleLot () {
      return this.route.vehicle_lot || {}
    },
    assignedVehicle () {
      return (this.selectedVehicle)
        ? this.selectedVehicle.license_plate
        : null
    },
    assignedDriver () {
      return (this.selectedDriver)
        ? `${this.selectedDriver.first_name} ${this.selectedDriver.last_name}`
        : null
    }
  },

  async beforeMount () {
    // Ensure assigned vehicle is loaded
    if (!this.route.vehicle && this.route.vehicle_id) {
      const vehicle = await this.$store.dispatch('sh/dataApi', {
        method: 'get',
        uri: `vehicle/${this.route.vehicle_id}`
      })
      if (vehicle) this.selectedVehicle = vehicle
    } else {
      this.selectedVehicle = this.route.vehicle
    }

    // Ensure assigned driver is loaded
    if (!this.route.driver && this.route.driver_user_id) {
      const driver = await this.$store.dispatch('sh/dataApi', {
        method: 'get',
        uri: `user/${this.route.driver_user_id}`
      })
      if (driver) this.selectedDriver = driver
    } else {
      this.selectedDriver = this.route.driver
    }

    // Set start time for UI
    if (this.route.scheduled_start_time) {
      this.selectedStartTime = this.$root.utils.format(this.route.scheduled_start_time, 'hh:mm')
    }
  },

  methods: {
    // Generate link data for route info page
    generateRouteInfoPageLink () {
      return {
        name: 'route-info-overview',
        params: { routeId: this.route.id }
      }
    },

    // Generate link data for order info page
    generateOrderInfoPageLink (order) {
      if (order && order.model) {
        return {
          name: 'order-info-overview',
          params: { orderId: order.id }
        }
      } else {
        return ''
      }
    },

    setStartTime () {
      const startTime = this.selectedStartTime.split(':')
      if (startTime.length === 2) {
        const hour = startTime[0]
        const minute = startTime[1]
        const startTimeAsDate = moment(this.deliveryDate).tz(this.defaultTimezone).set({ hour, minute }).toISOString()
        this.route.scheduled_start_time = startTimeAsDate
        this.$emit('startTimeSet', startTimeAsDate)
      }
      this.isStartTimeSelectorOpen = false
    },

    setAssignedVehicle (vehicle) {
      if (vehicle) {
        this.route.vehicle_id = vehicle.id // set route field
        this.selectedVehicle = vehicle // store for local persistence
      }
      this.isVehicleSelectorOpen = false
      this.emitDirty('vehicle_id')
    },

    removeAssignedVehicle () {
      this.route.vehicle_id = null // set route field
      this.selectedVehicle = null // remove local persistence
      this.emitDirty('vehicle_id')
    },

    setAssignedDriver (driver) {
      if (driver) {
        this.route.driver_user_id = driver.id // set route field
        this.selectedDriver = driver // store for local persistence
      }
      this.isDriverSelectorOpen = false
      this.emitDirty('driver_user_id')
    },

    removeAssignedDriver () {
      this.route.driver_user_id = null // set route field
      this.selectedDriver = null // remove local persistence
      this.emitDirty('driver_user_id')
    },

    // Emit dispatch action
    dispatchRoute () {
      this.$emit('dispatchRoute')
    },

    // Emit change occurred (with property name)
    emitDirty (property) {
      this.$emit('dirty', property)
    },

    async syncOrders () {
      if (!this.shouldSyncOrders) return false

      this.isOrderSyncing = true

      try {
        // Sync orders on route (ensure set to "SCHEDULED" status)
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `route/${this.route.id}/sync-orders`
        })
      } catch (err) {
        console.log('Error syncing orders =>', err)
      }

      this.isOrderSyncing = false

      this.$emit('updated') // emit updated state
    }
  }
}
</script>

<style>
  .route-item-editor {
    border-radius: 2px !important;
    border-width: 2px !important;
    padding: 8px 12px 16px 10px !important;
  }

  .route-item-editor .label {
    color: #9c9c9c;
    font-size: 14px;
  }

  .route-item-editor .divider {
    border-bottom: 1px solid #32395F;
    margin-top: 11px !important;
    margin-bottom: 10px !important;
    margin-left: 10px !important;
    margin-right: 10px !important;
  }

  /* ------------------------------------------------------------------------ */
  /* Delivery Date */
  /* ------------------------------------------------------------------------ */

  .delivery-date {
    font-size: 24px;
  }

  /* ------------------------------------------------------------------------ */
  /* Dispatch Button */
  /* ------------------------------------------------------------------------ */

  .btn-dispatch {
    width: 150px;
    height: 75px !important;
  }

  .dispatched-message {
    font-size: 20px;
    text-align: center;
    border: 2px solid #1A83A9;
    border-radius: 4px;
  }

  /* ------------------------------------------------------------------------ */
  /* Start Time */
  /* ------------------------------------------------------------------------ */

  .start-time {
    font-size: 18px;
    color: #ffffff;
    margin-right: 6px;
    margin-left: 24px;
  }

  .icon-change-start-time {
    font-size: 16px !important;
  }

  .time-selector .v-time-picker-title, .v-card__actions {
    justify-content: center;
  }

  /* ------------------------------------------------------------------------ */
  /* Return Time */
  /* ------------------------------------------------------------------------ */

  .return-time {
    font-size: 24px;
  }

  .return-time-subtext {
    font-size: 12px;
    color: #9c9c9c;
  }

  .icon-return-time-info {
    cursor: pointer;
    color: #9c9c9c !important;
    font-size: 19px !important;
    position: absolute !important;
    margin-left: 134px;
  }

  .icon-return-time-info:hover {
    color: #ffffff !important;
  }

  /* ------------------------------------------------------------------------ */
  /* Assign Vehicle */
  /* ------------------------------------------------------------------------ */

  .assigned-vehicle {
    font-size: 16px;
    color: #ffffff;
    /* margin-right: 6px; */
    /* margin-left: 24px; */
  }

  /* .assigned {
    font: normal normal normal 16px/19px Lato;
    color: #8DBF42;
    border: 1px solid #8DBF42;
    padding: 2px 4px 4px 2px;
    border-radius: 5px;
  } */

  .icon-change-vehicle {
    font-size: 16px !important;
  }

  /* ------------------------------------------------------------------------ */
  /* Assign Driver */
  /* ------------------------------------------------------------------------ */

  .assigned-driver {
    font-size: 16px;
    color: #ffffff;
    /* margin-right: 6px; */
    /* margin-left: 24px; */
  }

  /* .assigned {
    font: normal normal normal 16px/19px Lato;
    color: #8DBF42;
    border: 1px solid #8DBF42;
    padding: 2px 4px 4px 2px;
    border-radius: 5px;
  } */

  .icon-change-driver {
    font-size: 16px !important;
  }
</style>
